'use client';

import {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { GlobalUIContext } from 'contexts/GlobalUIContext';
import { useTranslations } from 'next-intl';
import styles from './ShopSidebar.module.css';

interface ShopSidebarProps {
  className?: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  categories: Array<{ key: string; label: string }>;
  activeCategories: Set<string>;
  onCategoryClick: (key: string) => void;
}

export const ShopSidebar = ({
  className,
  // searchQuery,
  // onSearchChange,
  categories,
  activeCategories,
  onCategoryClick,
}: ShopSidebarProps) => {
    const t = useTranslations('ring');
    const GlobalUI = useContext(GlobalUIContext);
    const categoryRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showLeftCaret, setShowLeftCaret] = useState(false);
    const [showRightCaret, setShowRightCaret] = useState(true);

    const updateCarets = useCallback(() => {
      const el = scrollRef.current;
      if (!el) return;
      setShowLeftCaret(el.scrollLeft > 0);
      setShowRightCaret(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      updateCarets();
      const observer = new ResizeObserver(updateCarets);
      observer.observe(el);
      return () => observer.disconnect();
    }, [updateCarets]);

    const scrollBy = (direction: number) => {
      scrollRef.current?.scrollBy({ left: direction * 200, behavior: 'smooth' });
    };

    const stickyTop = GlobalUI.headerVisible ? GlobalUI.headerHeight ?? 0 : 0;

    return (
      <aside
        className={[styles.root, className].filter(Boolean).join(' ')}
        style={{ '--shop-sidebar-top': `${stickyTop}px` } as CSSProperties}
      >
        <div className="categories-heading">{t('ring.shop.categories')}</div>

        <div className="category-pills-wrapper">
          <button
            className={`caret caret-left${showLeftCaret ? '' : ' hidden'}`}
            onClick={() => scrollBy(-1)}
            aria-label={t('ring.shop.scrollCategoriesLeft')}
          >
            ‹
          </button>
          <div
            className="category-pills u-hide-scrollbar"
            ref={scrollRef}
            onScroll={updateCarets}
          >
            {categories.map((cat) => (
              <button
                key={cat.key}
                ref={(el) => {
                  if (el) categoryRefs.current.set(cat.key, el);
                  else categoryRefs.current.delete(cat.key);
                }}
                className={activeCategories.has(cat.key) ? 'pill active' : 'pill'}
                onClick={() => {
                  onCategoryClick(cat.key);
                  categoryRefs.current.get(cat.key)?.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'nearest',
                    block: 'nearest',
                  });
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>
          <button
            className={`caret caret-right${showRightCaret ? '' : ' hidden'}`}
            onClick={() => scrollBy(1)}
            aria-label={t('ring.shop.scrollCategoriesRight')}
          >
            ›
          </button>
        </div>
      </aside>
    );
};
